import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { getCurrentUser, onAuthChange } from '../lib/localAuth';
import { setAuthContext, clearAuthContextCache } from '../lib/authContext';

interface AuthCacheUser {
  id: string;
  email: string;
  displayName?: string;
  stackAuthId: string;
}

interface AuthCacheContextType {
  user: AuthCacheUser | null;
  loading: boolean;
  refreshUser: () => Promise<void>;
}

const AuthCacheContext = createContext<AuthCacheContextType | undefined>(undefined);

// Cache duration: 30 minutes (matching authContext.ts)
const CACHE_DURATION = 30 * 60 * 1000;

export function AuthCacheProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthCacheUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastFetch, setLastFetch] = useState(0);

  const fetchUser = useCallback(async (force = false) => {
    // Check if we should use cache
    if (!force && user && Date.now() - lastFetch < CACHE_DURATION) {
      return;
    }

    try {
      const me = await getCurrentUser();
      if (me && me.id) {
        const authUser: AuthCacheUser = {
          id: me.id,
          email: me.email || '',
          displayName: me.displayName,
          stackAuthId: me.id,
        };
        
        setUser(authUser);
        setLastFetch(Date.now());
        
        // Update the authContext cache
        setAuthContext(null, me.id);
      } else {
        setUser(null);
        clearAuthContextCache();
      }
    } catch (error) {
      console.error('Error fetching user:', error);
      // Keep existing user if fetch fails
    } finally {
      setLoading(false);
    }
  }, [user, lastFetch]);

  const refreshUser = useCallback(async () => {
    await fetchUser(true);
  }, [fetchUser]);

  useEffect(() => {
    fetchUser();
  }, []);

  // Listen for auth state changes
  useEffect(() => {
    const handleAuthChange = () => {
      fetchUser(true);
    };

    const off = onAuthChange(handleAuthChange);
    return () => off();
  }, [fetchUser]);

  // Set up periodic refresh (every 25 minutes to ensure cache doesn't expire)
  useEffect(() => {
    const interval = setInterval(() => {
      fetchUser();
    }, 25 * 60 * 1000);

    return () => clearInterval(interval);
  }, [fetchUser]);

  return (
    <AuthCacheContext.Provider value={{ user, loading, refreshUser }}>
      {children}
    </AuthCacheContext.Provider>
  );
}

export function useAuthCache() {
  const context = useContext(AuthCacheContext);
  if (context === undefined) {
    throw new Error('useAuthCache must be used within an AuthCacheProvider');
  }
  return context;
}
